import { loadStripe } from '@stripe/stripe-js';
import { axiosCatchHandler } from "../sharedGlobal";

const SET_STRIPE = 'SET_STRIPE';
const SET_NEW_CARD_ELEMENT = 'SET_NEW_CARD_ELEMENT';
const SET_OPTIONS = 'SET_OPTIONS';
const SET_MERCHANT_ACCOUNT_ID = 'SET_MERCHANT_ACCOUNT_ID';
const SET_CARD_ERROR = 'SET_CARD_ERROR';
const SET_NEW_PAYMENT_METHOD = 'SET_NEW_PAYMENT_METHOD';
const SET_CLIENT_SECRET = 'SET_CLIENT_SECRET';
const SET_PROCESSING = 'SET_PROCESSING';
const SET_CARD_HOLDER_NAME = 'SET_CARD_HOLDER_NAME';

const state = {
  options: {},
  stripe: null,
  newCard: null,
  cardError: null,
  newPaymentMethod: null,
  clientSecret: null,
  processing: false,
  cardHolderName: null,
};

const getters = {
  options: ({ options }) => options,
  stripe: ({ stripe }) => stripe,
  newCard: ({ newCard }) => newCard,
  newPaymentMethod: ({ newPaymentMethod }) => newPaymentMethod,
  cardError: ({ cardError }) => cardError,
  clientSecret: ({ clientSecret }) => clientSecret,
  processing: ({ processing }) => processing,
  cardHolderName: ({ cardHolderName }) => cardHolderName,
};

const actions = {
  setCardHolderName({ commit }, { name }) {
    commit(SET_CARD_HOLDER_NAME, name);
  },
  async setupStripe({ commit, getters }, { clientSecret }) {
    let stripeOpts = getters.options;

    const stripe = await loadStripe(
      process.env.MIX_STRIPE_KEY,
      stripeOpts,
    );
    commit(SET_STRIPE, stripe);

    if (clientSecret) {
      commit(SET_CLIENT_SECRET, clientSecret);
    }
  },
  createCardElement({ commit, getters }, { element, options }) {
    const elements = getters.stripe.elements();
    const card = elements.create('card', options);
    card.mount(element);
    card.on('change', (event) => {
      commit(SET_CARD_ERROR, event.error ? event.error.message : null);
    });
    commit(SET_NEW_CARD_ELEMENT, card);
  },
  async storeCard({ commit, getters }) {
    commit(SET_CARD_ERROR, null);

    const { error } = await getters.stripe.confirmCardSetup(getters.clientSecret, {
      payment_method: {
        card: getters.newCard,
        billing_details: {
          name: getters.cardHolderName
        },
      },
    });

    if (error) {
      commit(SET_CARD_ERROR, error.message);
    }

    const { setupIntent } = await getters.stripe.retrieveSetupIntent(getters.clientSecret);

    commit(SET_NEW_PAYMENT_METHOD, setupIntent.payment_method);
  },
  async setCardAsDefault({ commit, getters }) {
    if (getters.cardError) {
      return;
    }

    if (!getters.newPaymentMethod) {
      throw new Error('Payment method not setup.');
    }

    await axios.post(`/billing/cards/${getters.newPaymentMethod}/set-default`)
      .then(response => {
        if (response.data.redirect) {
          window.location.assign(response.data.redirect);
        }
      });
  },
  async pay({ commit, getters }) {
    const { paymentIntent, error } = await getters.stripe.confirmCardPayment(getters.clientSecret, {
      payment_method: {
        card: getters.newCard,
        billing_details: {
          name: getters.cardHolderName
        }
      },
      setup_future_usage: 'off_session',
    })

    commit(SET_CARD_ERROR, error?.message);

    if (error) {
      return;
    }

    if (paymentIntent.status !== 'succeeded') {
      throw new Error('Payment failed.')
    }

    commit(SET_NEW_PAYMENT_METHOD, paymentIntent.payment_method);
  },
  async payAndSaveCardAsDefault({ commit, dispatch, getters }) {
    if (getters.processing) {
      return;
    }

    await commit(SET_PROCESSING, true);
    try {
      await dispatch('pay');
      await dispatch('setCardAsDefault');
    } catch (e) {
      // Catch any generic and unexpected exceptions and ignore if we have already set a Stripe related error.
      if (!getters.cardError) {
        commit(SET_CARD_ERROR, 'Something went wrong while adding your card, please try again.');
      }
    } finally {
      await commit(SET_PROCESSING, false);
    }
  },
  async createDefaultPaymentMethod({ commit, dispatch, getters }) {
    if (getters.processing) {
      return;
    }

    await commit(SET_PROCESSING, true);
    try {
      await dispatch('storeCard');
      await dispatch('setCardAsDefault');
    } catch (e) {
      if (!getters.cardError) {
        commit(SET_CARD_ERROR, 'Something went wrong while adding your card, please try again.');
      }
    } finally {
      await commit(SET_PROCESSING, false);
    }
  },
  async createStripeCustomer({ commit, getters }, selectedCurrency) {
    if (getters.processing) {
      return;
    }

    await commit(SET_PROCESSING, true);

    const { paymentMethod, error } = await getters.stripe.createPaymentMethod({
      type: 'card',
      card: getters.newCard,
      billing_details: {
        name: getters.cardHolderName
      },
    });

    if (error) {
      commit(SET_CARD_ERROR, error.message);
      return;
    }

    try {
      await axios.post('/ajax/create-customer', {
        currency: selectedCurrency,
        paymentMethodId: paymentMethod.id,
        returnUrl: null,
      });
    } catch(error) {
      if (!getters.cardError) {
        commit(SET_CARD_ERROR, 'Something went wrong while adding your card, please try again.');
      }
    } finally {
      await commit(SET_PROCESSING, false);
    }
  },
};

const mutations = {
  [SET_STRIPE](state, stripe) {
    state.stripe = stripe;
  },
  [SET_OPTIONS](state, options) {
    state.options = options;
  },
  [SET_MERCHANT_ACCOUNT_ID](state, merchantAccountId) {
    state.merchantAccountId = merchantAccountId;
  },
  [SET_NEW_CARD_ELEMENT](state, newCard) {
    state.newCard = newCard;
  },
  [SET_CARD_ERROR](state, error) {
    state.cardError = error;
  },
  [SET_NEW_PAYMENT_METHOD](state, paymentMethod) {
    state.newPaymentMethod = paymentMethod;
  },
  [SET_CLIENT_SECRET](state, clientSecret) {
    state.clientSecret = clientSecret;
  },
  [SET_PROCESSING](state, processing) {
    state.processing = processing;
  },
  [SET_CARD_HOLDER_NAME](state, name) {
    state.cardHolderName = name;
  },
};

const billing = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default billing;
